import type { EntreeFlotte } from './types'

/**
 * Les trois façons de choisir ce qu'on veut revoir — par véhicule, par livreur, par boîtier — ramenées au MÊME objet : le boîtier
 * (c'est lui qui porte l'historique). Le choix suit le rattachement EXISTANT (0026 / 0054) : un boîtier posé sur un véhicule ; un
 * boîtier rattaché à un livreur ; et le boîtier d'un véhicule localise aussi le livreur de ce véhicule.
 *
 * ⚠️ Le rattachement est celui d'AUJOURD'HUI : la base n'historise pas qui conduisait quel véhicule. L'historique d'un livreur
 * est donc celui du boîtier qui lui est rattaché maintenant ; si le boîtier a changé de véhicule ou de livreur depuis, les trajets
 * passés suivent le boîtier, pas la personne (voir docs/HISTORIQUE-ET-RAPPORTS-GPS.md § 8).
 */
export interface BoitierSimple {
  id: string
  uniqueId: string
  label: string | null
  vehicleId: string | null
  driverId: string | null
  active: boolean
}
export interface VehiculeSimple {
  id: string
  plate: string
  model?: string | null
  driverId?: string | null
}
export interface LivreurSimple {
  id: string
  nom: string
}

const net = (s: string | null | undefined) => (s ?? '').trim()

export function entreesFlotte(
  boitiers: readonly BoitierSimple[],
  vehicules: readonly VehiculeSimple[],
  livreurs: readonly LivreurSimple[],
): EntreeFlotte[] {
  const vehicule = (id: string | null) => (id ? vehicules.find((v) => v.id === id) : undefined)
  const livreur = (id: string | null | undefined) => (id ? livreurs.find((l) => l.id === id) : undefined)
  const libelleVehicule = (v: VehiculeSimple) => net(v.plate) || net(v.model) || 'Véhicule'
  const nomBoitier = (b: BoitierSimple) => net(b.label) || (vehicule(b.vehicleId) ? libelleVehicule(vehicule(b.vehicleId)!) : '') || b.uniqueId
  /** Le boîtier à préférer quand une entrée en a plusieurs : un boîtier actif d'abord. */
  const meilleur = (l: readonly BoitierSimple[]) => l.find((b) => b.active) ?? l[0]

  const sortie: EntreeFlotte[] = []

  // Par véhicule : un boîtier posé sur lui.
  for (const v of vehicules) {
    const b = meilleur(boitiers.filter((x) => x.vehicleId === v.id))
    if (!b) continue
    const conducteur = livreur(v.driverId ?? b.driverId)
    sortie.push({ boitierId: b.id, libelle: libelleVehicule(v), detail: conducteur?.nom ?? null, genre: 'vehicule', actif: b.active })
  }

  // Par livreur : son boîtier, ou celui de son véhicule.
  for (const l of livreurs) {
    const sesVehicules = new Set(vehicules.filter((v) => v.driverId === l.id).map((v) => v.id))
    const b = meilleur(boitiers.filter((x) => x.driverId === l.id || (!x.driverId && x.vehicleId !== null && sesVehicules.has(x.vehicleId))))
    if (!b) continue
    const v = vehicule(b.vehicleId)
    sortie.push({ boitierId: b.id, libelle: net(l.nom) || 'Livreur', detail: v ? libelleVehicule(v) : net(b.label) || null, genre: 'livreur', actif: b.active })
  }

  // Par boîtier : tous, désactivés compris (on peut vouloir revoir un boîtier retiré).
  for (const b of boitiers) {
    const v = vehicule(b.vehicleId)
    const l = livreur(b.driverId ?? v?.driverId)
    const detail = [v ? libelleVehicule(v) : null, l?.nom ?? null].filter((x): x is string => !!x && x !== nomBoitier(b)).join(' · ')
    sortie.push({ boitierId: b.id, libelle: nomBoitier(b), detail: detail || null, genre: 'boitier', actif: b.active })
  }
  return sortie
}
